import type { ComponentType } from 'react'
import type { DirectiveConfig } from '../content/directiveParser'

/**
 * Component Registry
 * ------------------
 * Maps directive names (from lesson Markdown) to registered React components.
 * Reusable interactions register here once; lesson-specific interactions can
 * register too. Rendering an *unknown* directive is handled by the caller with
 * a graceful fallback (see `LessonContent`), so a stale or mistyped directive
 * never breaks a lesson.
 */

export interface InteractionProps {
  /** Stable id within a lesson, used to key persisted responses. */
  id: string
  /** Small, validated configuration parsed from the directive body. */
  config: DirectiveConfig
  /** Previously saved response for this interaction, restored via lesson state. */
  savedResponse?: unknown
  /** Called when the learner's response changes; persisted by the lesson state layer. */
  onResponseChange?: (response: unknown) => void
  /** Called when the interaction's completion condition is met. */
  onComplete?: () => void
}

export type InteractionComponent = ComponentType<InteractionProps>

const registry = new Map<string, InteractionComponent>()

export function registerInteraction(name: string, component: InteractionComponent): void {
  registry.set(name, component)
}

export function getInteraction(name: string): InteractionComponent | null {
  return registry.get(name) ?? null
}

export function hasInteraction(name: string): boolean {
  return registry.has(name)
}

export function registeredNames(): string[] {
  return [...registry.keys()]
}
